import React from 'react';

import { Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes } from '@clearverify/constants';

import { useCreateUserPasswordResetRequestMutation } from 'src/api';
import { Button } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { SuccessfulCard } from './SuccessfulCard';
import { ForgotPasswordFormFields, ForgotPasswordFormValues } from './types';
import { forgotPasswordValidationSchema } from './utils/validationSchemas';

const initialValues: ForgotPasswordFormValues = {
  email: '',
};

export const ForgotPasswordForm: React.FC = () => {
  const [isSuccessfulSubmitted, setIsSuccessfulSubmitted] = React.useState(false);
  const navigate = useNavigate();
  const { spawnNotification, createApolloContext } = useNotification();

  const [createUserPasswordResetRequestMutation, { loading: requestCreating }] =
    useCreateUserPasswordResetRequestMutation({
      context: createApolloContext({
        successMessage: 'Reset Password Request Created Successfully. Check Your Email!',
      }),
    });

  const onSubmit = React.useCallback(
    async (formData: ForgotPasswordFormValues) => {
      try {
        const { data } = await createUserPasswordResetRequestMutation({
          variables: {
            email: String(formData.email),
          },
        });

        if (!data?.createUserPasswordResetRequest.success) {
          spawnNotification(
            'We can’t find Email address you specified. Make sure you entered a valid email',
            'error',
          );
        } else {
          setIsSuccessfulSubmitted(prev => !prev);
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
        }
      }
    },
    [createUserPasswordResetRequestMutation, spawnNotification],
  );

  if (isSuccessfulSubmitted) {
    return <SuccessfulCard />;
  }

  return (
    <Form<ForgotPasswordFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={forgotPasswordValidationSchema}
      subscription={{ submitting: true }}
    >
      {({ submitting }) => (
        <Grid container maxWidth={500}>
          <Grid>
            <Typography variant="h6" mt={1}>
              Let’s reset your password
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Type in the email address you used during registration and we&apos;ll send you a link
              to reset your current password
            </Typography>
          </Grid>

          <Grid container mt={3}>
            <Grid size={12}>
              <Field
                label="Email"
                component={TextField}
                name={ForgotPasswordFormFields.email}
                disabled={submitting}
                fullWidth
              />
            </Grid>

            <Grid container justifyContent="center" mt={4} gap={2} size={12}>
              <Grid>
                <Button
                  variant="outlined"
                  color="error"
                  loading={submitting || requestCreating}
                  disabled={submitting || requestCreating}
                  onClick={() => {
                    navigate(appRoutes.auth.login);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={submitting || requestCreating}
                  disabled={submitting || requestCreating}
                >
                  Send reset link
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
